import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BellRing,
  BookOpen,
  CalendarDays,
  ClipboardList,
  Clock,
  Contact,
  FileText,
  FolderKanban,
  Mail,
  MailPlus,
  Megaphone,
  MousePointerClick,
  QrCode,
  ScrollText,
  Send,
  ShieldAlert,
  SlidersHorizontal,
  Users,
} from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { adminOverviewQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/t1dwpanel/")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard | Warrior Volunteer" },
      { name: "description", content: "Admin Dashboard in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Admin Dashboard | Warrior Volunteer" },
      { property: "og:description", content: "Admin Dashboard in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDashboard,
});

const TRACK_LABELS: Record<string, string> = {
  remote_outreach: "Remote outreach",
  onsite_crew: "On-site crew",
  both: "Both tracks",
  unspecified: "Not set",
};

function formatDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function AdminDashboard() {
  const { data, isLoading } = useQuery(adminOverviewQuery);

  return (
    <AppShell eyebrow="Admin" title="Program dashboard">
      {isLoading ? <p className="t-body">Loading metrics...</p> : null}
      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Metric
              label="Pending applications"
              value={data.pendingApplications}
              icon={ClipboardList}
              to="/t1dwpanel/applications"
            />
            <Metric
              label="Active volunteers"
              value={data.activeVolunteers}
              icon={Users}
              to="/t1dwpanel/volunteers"
            />
            <Metric
              label="Sends this month"
              value={data.sendsThisMonth}
              icon={Mail}
              to="/t1dwpanel/outreach"
            />
            <Metric label="Hours logged" value={data.totalHours} icon={Clock} to="/t1dwpanel/activity" />
            <Metric
              label="In invite funnel"
              value={data.campaign.active}
              icon={Megaphone}
              to="/t1dwpanel/campaign"
            />
            <Metric
              label="Open Sentry issues"
              value={data.sentry.open}
              icon={ShieldAlert}
              to="/t1dwpanel/sentry"
            />
          </div>

          {data.pendingApplications || data.sentry.critical || data.draftEvents ? (
            <section className="panel mt-6 border-cyan/40 p-5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-cyan" />
                <h2 className="t-h3 text-ice">Needs your attention</h2>
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                {data.pendingApplications ? (
                  <li>
                    <Link to="/t1dwpanel/applications" className="font-semibold text-cyan">
                      {data.pendingApplications} application
                      {data.pendingApplications === 1 ? "" : "s"} waiting on review
                    </Link>
                  </li>
                ) : null}
                {data.draftEvents ? (
                  <li>
                    <Link to="/t1dwpanel/events" className="font-semibold text-cyan">
                      {data.draftEvents} event{data.draftEvents === 1 ? "" : "s"} still in draft
                    </Link>
                  </li>
                ) : null}
                {data.sentry.critical ? (
                  <li>
                    <Link to="/t1dwpanel/sentry" className="font-semibold text-cyan">
                      {data.sentry.critical} critical Sentry issue
                      {data.sentry.critical === 1 ? "" : "s"}
                    </Link>
                  </li>
                ) : null}
              </ul>
            </section>
          ) : null}

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <section className="panel p-6">
              <div className="flex items-center justify-between">
                <h2 className="t-h3 text-ice">Upcoming event coverage</h2>
                <Link to="/t1dwpanel/events" className="text-[0.7rem] font-semibold tracking-wide text-cyan">
                  Manage events
                </Link>
              </div>
              {data.upcomingEvents.length ? (
                <ul className="mt-4 space-y-4">
                  {data.upcomingEvents.map((event) => {
                    const pct = event.capacity
                      ? Math.min(100, Math.round((event.filled / event.capacity) * 100))
                      : 0;
                    return (
                      <li key={event.id}>
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="font-semibold text-ice">
                            {event.title}
                            {!event.isPublished ? (
                              <span className="ml-2 rounded-sm bg-secondary px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">
                                Draft
                              </span>
                            ) : null}
                          </span>
                          <span className="t-meta">{formatDate(event.eventDate)}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full rounded-sm bg-secondary">
                          <div className="h-full rounded-sm bg-cyan" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="t-meta mt-1">
                          {event.filled} of {event.capacity} spots filled across {event.slots} slot
                          {event.slots === 1 ? "" : "s"}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="t-body mt-3 text-sm">No upcoming event dates on the board.</p>
              )}
            </section>

            <section className="panel p-6">
              <div className="flex items-center justify-between">
                <h2 className="t-h3 text-ice">Applications waiting</h2>
                <Link
                  to="/t1dwpanel/applications"
                  className="text-[0.7rem] font-semibold tracking-wide text-cyan"
                >
                  Review all
                </Link>
              </div>
              {data.pendingList.length ? (
                <ul className="mt-4 space-y-3">
                  {data.pendingList.map((app: any) => (
                    <li key={app.id} className="border-l-2 border-cyan/70 pl-3">
                      <p className="text-sm font-semibold text-ice">{app.full_name}</p>
                      <p className="t-meta mt-1">
                        {[app.city, app.state].filter(Boolean).join(", ") || app.email}
                        {app.volunteer_track
                          ? ` · ${TRACK_LABELS[app.volunteer_track] ?? app.volunteer_track}`
                          : ""}
                        {` · ${new Date(app.created_at).toLocaleDateString()}`}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="t-body mt-3 text-sm">Nothing pending. Every application is reviewed.</p>
              )}

              <h3 className="t-h3 mt-6 text-ice">Recent spot claims</h3>
              {data.recentSignups.length ? (
                <ul className="mt-3 space-y-2">
                  {data.recentSignups.map((signup: any) => (
                    <li key={signup.id} className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-ice">
                        {signup.full_name}
                        <span className="t-meta block">
                          {signup.event_slots?.event_dates?.events?.title ?? "Event"} ·{" "}
                          {signup.event_slots?.title ?? "Slot"}
                        </span>
                      </span>
                      <span className="t-meta shrink-0">
                        {new Date(signup.created_at).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="t-body mt-3 text-sm">No spot claims yet.</p>
              )}
            </section>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            <section className="panel p-6">
              <div className="flex items-center justify-between">
                <h2 className="t-h3 text-ice">Recruit volunteers</h2>
                <Megaphone className="h-4 w-4 text-cyan" />
              </div>
              <div className="mt-3 flex gap-8">
                <div>
                  <p className="text-3xl font-bold tabular-nums text-ice">{data.campaign.active}</p>
                  <p className="t-meta mt-1">in sequence</p>
                </div>
                <div>
                  <p className="text-3xl font-bold tabular-nums text-ice">
                    {data.campaign.converted}
                  </p>
                  <p className="t-meta mt-1">signed up</p>
                </div>
              </div>
              <dl className="mt-4 space-y-2 text-sm">
                <Row label="Total contacts" value={String(data.campaign.total)} />
                <Row label="Unsubscribed" value={String(data.campaign.unsubscribed)} />
                <Row label="Interest list" value={String(data.interestCount)} />
              </dl>
              <Link
                to="/t1dwpanel/campaign"
                className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
              >
                Open recruiting <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </section>

            <section className="panel p-6">
              <div className="flex items-center justify-between">
                <h2 className="t-h3 text-ice">Volunteers by track</h2>
                <Users className="h-4 w-4 text-cyan" />
              </div>
              {data.volunteersByTrack.length ? (
                <ul className="mt-4 space-y-3">
                  {data.volunteersByTrack.map((row) => {
                    const max = data.volunteersByTrack[0]?.count || 1;
                    return (
                      <li key={row.track}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-ice">{TRACK_LABELS[row.track] ?? row.track}</span>
                          <span className="tabular-nums font-semibold text-cyan">{row.count}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full rounded-sm bg-secondary">
                          <div
                            className="h-full rounded-sm bg-cyan"
                            style={{ width: `${Math.round((row.count / max) * 100)}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="t-body mt-3 text-sm">No active volunteers yet.</p>
              )}
            </section>

            <section className="panel p-6">
              <div className="flex items-center justify-between">
                <h2 className="t-h3 text-ice">Hours and outreach</h2>
                <Clock className="h-4 w-4 text-cyan" />
              </div>
              <p className="mt-3 text-3xl font-bold tabular-nums text-ice">{data.totalHours}</p>
              <p className="t-meta mt-1">total hours logged</p>
              <dl className="mt-4 space-y-2 text-sm">
                <Row label="Hours this month" value={`${data.hoursThisMonth} hr`} />
                <Row label="Sends this month" value={String(data.sendsThisMonth)} />
                <Row label="Outreach contacts" value={String(data.totalContacts)} />
              </dl>
              <Link
                to="/t1dwpanel/activity"
                className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
              >
                See all activity <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </section>
          </div>

          <section className="panel mt-8 p-6">
            <h2 className="t-h3 text-ice">Outreach by program</h2>
            {data.outreachByProgram.length === 0 ? (
              <p className="t-body mt-3 text-sm">No outreach contacts logged yet.</p>
            ) : (
              <ul className="mt-5 space-y-3">
                {data.outreachByProgram.map((row) => {
                  const max = data.outreachByProgram[0]?.count || 1;
                  return (
                    <li key={row.name}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ice">{row.name}</span>
                        <span className="tabular-nums font-semibold text-cyan">{row.count}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full rounded-sm bg-secondary">
                        <div
                          className="h-full rounded-sm bg-cyan"
                          style={{ width: `${Math.round((row.count / max) * 100)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="mt-8">
            <h2 className="t-h3 text-ice">Everywhere you can go</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile
                to="/t1dwpanel/applications"
                icon={ClipboardList}
                title="Applications"
                body="Review, approve or decline new volunteer applications."
              />
              <Tile
                to="/t1dwpanel/volunteers"
                icon={Users}
                title="Volunteers"
                body="Roster, consent status, tracks and volunteer detail."
              />
              <Tile
                to="/t1dwpanel/events"
                icon={CalendarDays}
                title="Events"
                body="Dates, slots, coverage, rosters, sharing and QR codes."
              />
              <Tile
                to="/t1dwpanel/campaign"
                icon={Megaphone}
                title="Recruit volunteers"
                body="Sequence contacts, preview emails and read the analytics."
              />
              <Tile
                to="/t1dwpanel/outreach"
                icon={Send}
                title="Inbox"
                body="Every outgoing kit email and every reply in one place."
              />
              <Tile
                to="/t1dwpanel/link-analytics"
                icon={MousePointerClick}
                title="Link analytics"
                body="Which email links get clicked, by whom, and who sent them."
              />
              <Tile
                to="/t1dwpanel/qr-analytics"
                icon={QrCode}
                title="QR analytics"
                body="Scans of every event, volunteer and landing QR code."
              />
              <Tile
                to="/t1dwpanel/contacts"
                icon={Contact}
                title="Contacts"
                body="All contacts across volunteers, with touch history."
              />
              <Tile
                to="/t1dwpanel/kits"
                icon={BookOpen}
                title="Kit editor"
                body="Emails, call scripts, texts and one pagers per program."
              />
              <Tile
                to="/t1dwpanel/email-templates"
                icon={FileText}
                title="Email templates"
                body="Shared and private templates volunteers can send."
              />
              <Tile
                to="/t1dwpanel/programs"
                icon={FolderKanban}
                title="Programs"
                body="Program list, summaries and assignment targets."
              />
              <Tile
                to="/t1dwpanel/notifications"
                icon={MailPlus}
                title="Email notifications"
                body="Who gets alerted for applications, events and signups."
              />
              <Tile
                to="/t1dwpanel/notify-list"
                icon={BellRing}
                title="Notify list"
                body="Interest list of people waiting to hear about events."
              />
              <Tile
                to="/t1dwpanel/settings"
                icon={SlidersHorizontal}
                title="Event defaults"
                body="Default contact info and BCC settings for outings."
              />
              <Tile
                to="/t1dwpanel/activity"
                icon={Activity}
                title="Activity"
                body="Logged hours across every volunteer and source."
              />
              <Tile
                to="/t1dwpanel/audit"
                icon={ScrollText}
                title="Audit log"
                body="Who changed what, when, across the admin panel."
              />
              <Tile
                to="/t1dwpanel/sentry"
                icon={ShieldAlert}
                title="Sentry"
                body="Triaged error inbox with briefs and fix prompts."
              />
            </div>
          </section>
        </>
      ) : null}
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="t-meta">{label}</dt>
      <dd className="tabular-nums font-semibold text-ice">{value}</dd>
    </div>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
  to,
}: {
  label: string;
  value: number;
  icon: typeof Users;
  to: string;
}) {
  return (
    <Link to={to} className="panel p-5 transition-colors hover:border-cyan/60">
      <div className="flex items-center justify-between">
        <p className="t-meta">{label}</p>
        <Icon className="h-4 w-4 text-cyan" />
      </div>
      <p className="mt-3 text-3xl font-bold tabular-nums text-ice">{value}</p>
    </Link>
  );
}

function Tile({
  to,
  icon: Icon,
  title,
  body,
}: {
  to: string;
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <Link to={to} className="panel flex flex-col gap-2 p-5 transition-colors hover:border-cyan/60">
      <Icon className="h-4 w-4 text-cyan" />
      <p className="text-sm font-semibold text-ice">{title}</p>
      <p className="t-body text-xs">{body}</p>
    </Link>
  );
}
